import { Commit, Dispatch } from 'vuex'
import { isApiError } from '@/types/api'
import { ModelPricingLookupItem, ModelPricingLookupResult } from '@/types/modelPricing'
import { queryModelPricing } from '@/utils/api'
import { modelPricingCacheKey } from '@/utils/modelPricing'

export interface PricingState {
  lookup: Record<string, ModelPricingLookupResult>
  loadingKeys: Record<string, boolean>
}

export const pricing = () => ({
  state: <PricingState>{
    lookup: {},
    loadingKeys: {}
  },
  mutations: {
    setPricingResults(state: PricingState, results: ModelPricingLookupResult[]) {
      for (const result of results) {
        state.lookup[modelPricingCacheKey(result.providerKey, result.normalizedModel)] = result
      }
    },
    setPricingLoading(state: PricingState, { keys, loading }: { keys: string[]; loading: boolean }) {
      for (const key of keys) {
        state.loadingKeys[key] = loading
      }
    }
  },
  actions: {
    async fetchModelPricing(
      { commit, dispatch, state }: { commit: Commit; dispatch: Dispatch; state: PricingState },
      { items, force = false }: { items: ModelPricingLookupItem[]; force?: boolean }
    ) {
      const deduped = new Map<string, ModelPricingLookupItem>()
      for (const item of items) {
        const providerKey = String(item.providerKey || '').trim()
        const model = String(item.model || '').trim()
        if (!providerKey || !model) continue
        deduped.set(modelPricingCacheKey(providerKey, model), { providerKey, model })
      }

      const pending = Array.from(deduped.entries())
        .filter(([key]) => force || state.lookup[key] == null)
        .map(([, item]) => item)

      if (pending.length === 0) return

      const loadingKeys = pending.map((item) => modelPricingCacheKey(item.providerKey, item.model))
      commit('setPricingLoading', { keys: loadingKeys, loading: true })

      const response = await queryModelPricing({ models: pending })
      commit('setPricingLoading', { keys: loadingKeys, loading: false })

      if (isApiError(response)) {
        dispatch('updateSnackbar', { text: `Model Pricing Error: ${response.message}`, color: 'error' }, { root: true })
        dispatch('addLogEvent', { title: 'Model Pricing Error', content: response }, { root: true })
        return
      }

      if (response.success) {
        commit('setPricingResults', response.results)
      }
    }
  },
  getters: {
    modelPricingByProviderModel:
      (state: PricingState) =>
      (providerKey: string, model: string): ModelPricingLookupResult | undefined =>
        state.lookup[modelPricingCacheKey(providerKey, model)],
    modelPricingLoading:
      (state: PricingState) =>
      (providerKey: string, model: string): boolean =>
        state.loadingKeys[modelPricingCacheKey(providerKey, model)] === true
  }
})
